import type { DateTime } from 'luxon';
import { z } from 'zod';
import type {
  AppointmentSource,
  AppointmentStatus,
  ServiceFlowMode,
} from '../enums';

// Input validation schemas - Frontend versions aligned with backend
export const createAppointmentSchema = z.object({
  userId: z.string(),
  providerId: z.string(),
  shopId: z.string().optional(),
  scheduledStart: z.string(), // ISO date string
  duration: z.number().int().positive(),
  services: z.array(
    z.object({
      serviceId: z.string(),
      servicePrice: z.number().positive(),
    }),
  ),
  paymentIntentId: z.string().optional(),
  // Populated when the PI was created on a connected account (owner-routed
  // direct charges). Forwarded to the backend so it can retrieve the PI with
  // the correct Stripe account context.
  stripeAccount: z.string().optional(),
  customerName: z.string().optional(),
  customerPhone: z.string().optional(),
  styleNotes: z.string().max(500).optional(),
});

export const updateAppointmentSchema = z.object({
  status: z.enum([
    'SCHEDULED',
    'WAITING',
    'IN_PROGRESS',
    'COMPLETED',
    'CANCELLED',
    'NO_SHOW',
  ]),
  actualStart: z.string().optional(), // ISO date string
  actualEnd: z.string().optional(), // ISO date string
});

export const cancelAppointmentSchema = z.object({
  reason: z.string().optional(),
});

export const rescheduleAppointmentSchema = z.object({
  scheduledStart: z.string(), // ISO date string
  duration: z.number().int().positive().optional(), // Made optional to match backend
  reason: z.string().optional(), // Added to match backend
  actionBy: z.enum(['PROVIDER', 'USER']),
  actorId: z.string(),
});

// Type definitions - Frontend DTOs
export type CreateAppointmentDTO = z.infer<typeof createAppointmentSchema>;
export type UpdateAppointmentDTO = z.infer<typeof updateAppointmentSchema>;
export type CancelAppointmentDTO = z.infer<typeof cancelAppointmentSchema>;
export type RescheduleAppointmentDTO = z.infer<
  typeof rescheduleAppointmentSchema
>;

// Parameter interfaces for hook functions
export interface RescheduleAppointmentParams {
  appointmentId: string;
  newDateTime: DateTime; // Luxon DateTime
  duration: number;
  reason?: string;
  asProvider?: boolean;
}

export interface CancelAppointmentParams {
  appointmentId: string;
  reason: string;
}

// Interface for service data
export interface AppointmentService {
  id: string;
  appointmentId: string;
  serviceId: string;
  servicePrice: string;
  service: {
    id: string;
    name: string;
    price: string;
    duration: number;
    description?: string;
    imageUrl?: string;
    shopId: string;
    providerId: string;
    categoryId: string;
    serviceType: string;
    isPopular?: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

// Basic Appointment interface
export interface Appointment {
  id: string;
  userId: string;
  providerId: string;
  shopId: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  duration: number;
  status: AppointmentStatus;
  appointmentSource?: AppointmentSource;
  createdByProviderId?: string | null;
  clientStyleNotes?: string | null;
  flowModeResolved?: ServiceFlowMode | null;
  services: AppointmentService[];
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string | null;
    email: string;
    phoneNumber: string | null;
    pictureUrl: string | null;
  };
}

// Walk-in DTOs
export interface WalkInIntakeDTO {
  providerId: string;
  userId?: string;
  customerName?: string;
  customerPhone?: string;
  shopId?: string;
  scheduledStart: string;
  duration: number;
  services: { serviceId: string; servicePrice: number }[];
  styleNotes?: string;
}

export interface WalkInCompleteDTO {
  payment: {
    amount: number;
    tipAmount?: number;
    type: 'CASH';
  };
}

export interface WalkInCancelDTO {
  reason?: string;
}

// First approach: Modify AppointmentWithRelations

export interface AppointmentWithRelations
  extends Omit<Appointment, 'services'> {
  services: {
    id: string;
    appointmentId: string;
    serviceId: string;
    servicePrice: number;
    service: {
      id: string;
      name: string;
      price: number;
      duration: number;
    };
  }[];
  user: {
    id: string;
    name: string | null;
    email: string;
    phoneNumber: string | null;
    pictureUrl: string | null;
  };
  provider: {
    id: string;
    name: string;
    email: string | null;
    phoneNumber: string | null;
  };
  shop?: {
    id: string;
    name: string;
    timezone: string;
  } | null;
  payment?: {
    id: string;
    amount: number;
    status: string;
  } | null;
  reminders: {
    id: string;
    appointmentId: string;
    sent: boolean;
    sentAt: string | null;
    createdAt: string;
    updatedAt: string;
  }[];
}

export interface AppointmentFilters {
  start?: string;
  end?: string;
  status?: AppointmentStatus;
}

export interface QueuePosition {
  appointmentId: string;
  queuePosition: number;
  totalInQueue: number;
  currentlyServing: number;
  estimatedWaitTime: number;
  isRunningLate: boolean;
  delayMinutes: number;
  lastUpdated: string;
  inQueue: boolean;
  message?: string;
  isFutureAppointment?: boolean;
  appointmentDate?: string | null;
  isWaitingForProvider?: boolean;
  countdownMode?: 'authoritative' | 'legacy';
  shouldShowCountdown?: boolean;
  countdownRemainingSeconds?: number;
}

export interface QueueDetails extends QueuePosition {
  appointmentTime: string;
  duration: number;
  service: string;
  serviceImage?: string | null;
  providerName: string;
  providerAvatar: string | null;
  providerPhoneNumber?: string | null;
  providerTimezone?: string;
  status: AppointmentStatus;
  providerId?: string;
  date?: string;
}

// Appointment tracking for analytics
export interface AppointmentTracking {
  id: string;
  appointmentId: string;
  actualStartTime?: string | null;
  actualEndTime?: string | null;
  noShowTime?: string | null;
  cancelledTime?: string | null;
  checkInTime?: string | null;
  completedTime?: string | null;
  notifiedTime?: string | null;
  timeSaved?: number | null;
  viewCount?: number;
  createdAt: string;
  updatedAt: string;
}
